import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  MoreHorizontal,
  Ban,
  UserX,
  UserCheck,
  Trash2,
  Crown,
  Loader2,
  X,
  AlertTriangle,
  Eye,
  LogOut
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const API = "/api";

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionModal, setActionModal] = useState(null); // "suspend", "ban", "delete", "plan"
  const [actionReason, setActionReason] = useState("");
  const [newPlan, setNewPlan] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const adminEmail = localStorage.getItem("admin_email");

  // Handle user emulation
  const handleEmulateUser = async (user) => {
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API}/admin/users/${user.user_id}/emulate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || "Failed to emulate user");
        return;
      }
      
      const data = await response.json();
      
      // Store original admin token and info for returning later
      localStorage.setItem("admin_original_token", token);
      localStorage.setItem("admin_original_email", adminEmail);
      
      // Set the emulation token as the user's auth token
      localStorage.setItem("auth_token", data.emulation_token);
      localStorage.setItem("user", JSON.stringify({
        user_id: data.user.user_id,
        email: data.user.email,
        name: data.user.name,
        plan: data.user.plan,
        emulated: true,
        emulated_by: adminEmail
      }));
      
      // Set flag for emulation mode
      localStorage.setItem("emulation_mode", "true");
      localStorage.setItem("emulated_user_email", data.user.email);
      
      toast.success(data.message);
      
      // Navigate to the chat page as the user
      navigate("/chat");
    } catch (error) {
      console.error("Error emulating user:", error);
      toast.error("Failed to start emulation");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchUsers();
  }, [navigate, page, search, planFilter, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "25"
      });
      
      if (search) params.append("search", search);
      if (planFilter !== "all") params.append("plan", planFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);

      const response = await fetch(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
        return;
      }

      const data = await response.json();
      setUsers(data.users || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedUser || !actionModal) return;
    
    setActionLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      let endpoint = "";
      let method = "PATCH";
      let body = {};

      switch (actionModal) {
        case "suspend":
          endpoint = `/admin/users/${selectedUser.user_id}/status`;
          body = { status: "suspended", reason: actionReason };
          break;
        case "ban":
          endpoint = `/admin/users/${selectedUser.user_id}/status`;
          body = { status: "banned", reason: actionReason };
          break;
        case "reactivate":
          endpoint = `/admin/users/${selectedUser.user_id}/status`;
          body = { status: "active" };
          break;
        case "delete":
          endpoint = `/admin/users/${selectedUser.user_id}`;
          method = "DELETE";
          break;
        case "plan":
          endpoint = `/admin/users/${selectedUser.user_id}/plan`;
          body = { plan: newPlan };
          break;
        default:
          return;
      }

      const response = await fetch(`${API}${endpoint}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: method !== "DELETE" ? JSON.stringify(body) : undefined
      });

      if (!response.ok) {
        throw new Error("Action failed");
      }

      toast.success(`User ${actionModal === "delete" ? "deleted" : "updated"} successfully`);
      setActionModal(null);
      setSelectedUser(null);
      setActionReason("");
      setNewPlan("");
      fetchUsers();
    } catch (error) {
      toast.error(`Failed to ${actionModal} user`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/login");
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-500/20 text-green-400 border-green-500/30",
      suspended: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      banned: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    return styles[status] || styles.active;
  };

  const getPlanBadge = (plan) => {
    if (!plan || plan.toLowerCase().includes("free")) {
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
    if (plan.toLowerCase().includes("pro")) {
      return "bg-purple-500/20 text-purple-400 border-purple-500/30";
    }
    return "bg-blue-500/20 text-blue-400 border-blue-500/30";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Page Header */}
      <div className="border-b border-white/5 bg-[#0a0a0a]/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="px-8 py-6">
          <h1 className="text-2xl font-semibold text-white tracking-tight">Users</h1>
          <p className="text-zinc-500 text-sm mt-1">{total} total users</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 bg-[#121212] border-white/5 text-white placeholder:text-zinc-600"
            />
          </div>
          
          <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[150px] bg-[#121212] border-white/5 text-white">
              <SelectValue placeholder="All Plans" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all" className="text-white">All Plans</SelectItem>
              <SelectItem value="free" className="text-white">Free</SelectItem>
              <SelectItem value="pro" className="text-white">Pro</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[150px] bg-[#121212] border-white/5 text-white">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              <SelectItem value="active" className="text-white">Active</SelectItem>
              <SelectItem value="suspended" className="text-white">Suspended</SelectItem>
              <SelectItem value="banned" className="text-white">Banned</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        <div className="bg-[#121212] border border-white/5 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a0a0a] border-b border-white/5">
                <tr>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">User</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Company</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Plan</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Status</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Conversations</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Joined</th>
                  <th className="text-right text-[#888] text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-[#555] mx-auto" />
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-[#555]">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.user_id} className="border-b border-[#222] hover:bg-[#1a1a1a] transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-medium">{user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || "No Name"}</p>
                          <p className="text-[#666] text-sm">{user.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[#888]">
                        {user.company_name || "-"}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full border ${getPlanBadge(user.plan)}`}>
                          {user.plan || "Free Plan"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full border ${getStatusBadge(user.status)}`}>
                          {user.status || "active"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[#888]">
                        {user.conversation_count || 0}
                      </td>
                      <td className="px-6 py-4 text-[#888]">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Direct Delete Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedUser(user); setActionModal("delete"); }}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10 p-2"
                            data-testid={`delete-user-${user.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          
                          {/* More Actions Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-[#888] hover:text-white">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#111] border-[#333]">
                              <DropdownMenuItem
                                onClick={() => handleEmulateUser(user)}
                                className="text-blue-400 focus:text-blue-400 focus:bg-blue-400/10"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Emulate User
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-[#333]" />
                              <DropdownMenuItem
                                onClick={() => { setSelectedUser(user); setNewPlan(user.plan || "Free Plan"); setActionModal("plan"); }}
                                className="text-[#888] focus:text-white focus:bg-[#222]"
                              >
                                <Crown className="w-4 h-4 mr-2" />
                                Change Plan
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-[#333]" />
                              {user.status === "active" ? (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => { setSelectedUser(user); setActionModal("suspend"); }}
                                    className="text-yellow-400 focus:text-yellow-400 focus:bg-yellow-400/10"
                                  >
                                    <UserX className="w-4 h-4 mr-2" />
                                    Suspend
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => { setSelectedUser(user); setActionModal("ban"); }}
                                    className="text-red-400 focus:text-red-400 focus:bg-red-400/10"
                                  >
                                    <Ban className="w-4 h-4 mr-2" />
                                    Ban
                                  </DropdownMenuItem>
                                </>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => { setSelectedUser(user); setActionModal("reactivate"); }}
                                  className="text-green-400 focus:text-green-400 focus:bg-green-400/10"
                                >
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Reactivate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#222]">
              <p className="text-[#666] text-sm">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="text-[#888] hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="text-[#888] hover:text-white disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Confirmation Modal */}
      <Dialog open={!!actionModal} onOpenChange={() => { setActionModal(null); setSelectedUser(null); setActionReason(""); }}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionModal === "delete" && <AlertTriangle className="w-5 h-5 text-red-500" />}
              {actionModal === "suspend" && <UserX className="w-5 h-5 text-yellow-500" />}
              {actionModal === "ban" && <Ban className="w-5 h-5 text-red-500" />}
              {actionModal === "reactivate" && <UserCheck className="w-5 h-5 text-green-500" />}
              {actionModal === "plan" && <Crown className="w-5 h-5 text-purple-500" />}
              {actionModal === "delete" ? "Delete User" : 
               actionModal === "suspend" ? "Suspend User" :
               actionModal === "ban" ? "Ban User" :
               actionModal === "reactivate" ? "Reactivate User" :
               actionModal === "plan" ? "Change Plan" : ""}
            </DialogTitle>
            <DialogDescription className="text-[#888]">
              {selectedUser && (
                <>
                  {actionModal === "delete" && `This will permanently delete ${selectedUser.email} and all their data.`}
                  {actionModal === "suspend" && `This will suspend ${selectedUser.email}'s account. They will not be able to log in.`}
                  {actionModal === "ban" && `This will permanently ban ${selectedUser.email}. They will not be able to log in or re-register.`}
                  {actionModal === "reactivate" && `This will reactivate ${selectedUser.email}'s account.`}
                  {actionModal === "plan" && `Change the plan for ${selectedUser.email}.`}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {(actionModal === "suspend" || actionModal === "ban") && (
            <div className="py-4">
              <label className="text-sm text-[#888] mb-2 block">Reason (optional)</label>
              <Input
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Enter reason..."
                className="bg-[#0a0a0a] border-[#333] text-white"
              />
            </div>
          )}

          {actionModal === "plan" && (
            <div className="py-4">
              <label className="text-sm text-[#888] mb-2 block">New Plan</label>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-[#333]">
                  <SelectItem value="Free Plan" className="text-white">Free Plan</SelectItem>
                  <SelectItem value="Pro Plan" className="text-white">Pro Plan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => { setActionModal(null); setSelectedUser(null); }}
              className="text-[#888] hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={actionLoading}
              className={
                actionModal === "delete" || actionModal === "ban" ? "bg-red-600 hover:bg-red-700" :
                actionModal === "suspend" ? "bg-yellow-600 hover:bg-yellow-700" :
                actionModal === "reactivate" ? "bg-green-600 hover:bg-green-700" :
                "bg-purple-600 hover:bg-purple-700"
              }
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
